import React, { useMemo, useState } from "react"
import { Alert, Checkbox, Modal, Tag } from "antd"
import type { Editor as TiptapEditor } from "@tiptap/react"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { notify } from "shared/ui/Notifications/notify"
import { useAssistantStore } from "../assistantStore"
import { errorLinePrefix } from "../components/ProposalCard.component"
import type { ProposalOf, QnrEdit } from "../types"
import { applyQnrEdits } from "./applyQnrEdits"
import { linesWithMarks } from "./editorLines"

export const WORDING_BADGE = "Respondent wording changed"
export const FORMATTING_NOTE =
  "Replaced lines lose their formatting (bold, italic) and comments."

/** The variant's name as the editor shows it ("" is the master). */
const variantLabel = (variant: string): string => variant || "Master"

/**
 * Why AI edits can't go in now: the editor is read-only while its connection
 * is down (Editor.component), and changes made then would be lost.
 */
export const EDITOR_RECONNECTING_TEXT = "The editor is reconnecting; try again in a moment."

export const switchVariantText = (variant: string): string =>
  `Switch to ${variantLabel(variant)} to review`

/** The toast after Apply when some chosen changes could not be made. */
export const skippedText = (n: number): string =>
  `${n} ${n === 1 ? "change" : "changes"} no longer matched the editor and ${
    n === 1 ? "was" : "were"
  } skipped`

/**
 * Where each edit's new lines start in the questionnaire after all the
 * proposal's edits (the numbering its check errors use): its `fromLine`,
 * moved by the lines the edits above it add or remove.
 */
const startsAfterChange = (edits: QnrEdit[]): number[] => {
  const order = edits
    .map((edit, index) => ({ edit, index }))
    .sort((a, b) => a.edit.fromLine - b.edit.fromLine || a.index - b.index)
  const starts: number[] = new Array(edits.length)
  let shift = 0
  for (const { edit, index } of order) {
    starts[index] = edit.fromLine + shift
    shift += edit.newLines.length - edit.oldLines.length
  }
  return starts
}

/** What an edit does, in a few words. */
const editTitle = ({ fromLine, toLine, oldLines, newLines }: QnrEdit): string => {
  if (oldLines.length === 0) return `Insert before line ${fromLine}`
  const lines = fromLine === toLine ? `line ${fromLine}` : `lines ${fromLine}-${toLine}`
  return newLines.length === 0 ? `Remove ${lines}` : `Change ${lines}`
}

const DiffLine: React.FC<{ number: number; text: string; removed: boolean }> = ({
  number,
  text,
  removed,
}) => (
  <div
    className={`flex font-mono text-xs ${
      removed ? "bg-red-50 text-red-700" : "bg-green-50 text-green-800"
    }`}
    data-testid={removed ? "removed-line" : "added-line"}
  >
    <span className="w-10 shrink-0 select-none pr-2 text-right text-gray-400">{number}</span>
    <span className="w-4 shrink-0 select-none">{removed ? "-" : "+"}</span>
    <span className={`whitespace-pre-wrap break-all ${removed ? "line-through" : ""}`}>
      {text || " "}
    </span>
  </div>
)

interface ReviewBodyProps {
  proposal: ProposalOf<"qnr-edit">
  projectId: string
  currentVariant: string
  getEditor: () => TiptapEditor | null
  onClose: () => void
}

const ReviewBody: React.FC<ReviewBodyProps> = ({
  proposal,
  projectId,
  currentVariant,
  getEditor,
  onClose,
}) => {
  const { edits, validation } = proposal
  const [chosen, setChosen] = useState<boolean[]>(() => edits.map(() => true))
  const otherVariant = proposal.variant !== currentVariant
  const afterStarts = useMemo(() => startsAfterChange(edits), [edits])

  // Read once when the review opens: the open lines that carry formatting
  const marked = useMemo(() => {
    const editor = otherVariant ? null : getEditor()
    return editor ? linesWithMarks(editor) : new Set<number>()
  }, [otherVariant, getEditor])
  const losesFormatting = edits.some(
    (edit, i) =>
      chosen[i] &&
      Array.from({ length: edit.oldLines.length }, (_, k) => edit.fromLine + k).some((n) =>
        marked.has(n)
      )
  )

  const setStatus = (status: "accepted" | "rejected" | "stale"): void =>
    useAssistantStore.getState().setProposalStatus(projectId, proposal.id, status)

  const apply = (): void => {
    if (otherVariant) return
    const picked = edits.filter((_, i) => chosen[i])
    if (picked.length === 0) {
      // Every change unticked: the proposal is turned down
      setStatus("rejected")
      onClose()
      return
    }
    const editor = getEditor()
    if (!editor) {
      notify.toast.error("The editor isn't ready yet. Try again in a moment.")
      return
    }
    // A dispatched change goes in even when the editor is read-only: refused here instead
    if (!editor.isEditable) {
      notify.toast.error(EDITOR_RECONNECTING_TEXT)
      return
    }
    const { applied, stale } = applyQnrEdits(editor, picked)
    setStatus(applied.length > 0 ? "accepted" : "stale")
    if (stale.length > 0) notify.toast.warning(skippedText(stale.length))
    onClose()
  }

  const setAll = (value: boolean): void => setChosen(edits.map(() => value))

  return (
    <Modal
      open
      title="Review the assistant's changes"
      width={860}
      onCancel={onClose}
      destroyOnHidden
      footer={
        <div className="flex items-center gap-2">
          <DataQButton type="secondary" onClick={() => setAll(true)}>
            Accept all
          </DataQButton>
          <DataQButton type="secondary" onClick={() => setAll(false)}>
            Reject all
          </DataQButton>
          <div className="flex-1" />
          <DataQButton type="secondary" onClick={onClose}>
            Cancel
          </DataQButton>
          <DataQButton type="primary" isDisabled={otherVariant} onClick={apply}>
            Apply
          </DataQButton>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        {otherVariant && (
          <Alert type="info" showIcon message={switchVariantText(proposal.variant)} />
        )}
        {!validation.ok && (
          <Alert
            type="error"
            showIcon
            message="Not working: the questionnaire with these changes did not pass the check"
            description={
              validation.errors.length > 0 && (
                <ul className="m-0 pl-4 list-disc">
                  {validation.errors.map((e, i) => (
                    <li key={i}>
                      {errorLinePrefix(proposal, e.line)}
                      {e.message}
                    </li>
                  ))}
                </ul>
              )
            }
          />
        )}
        {losesFormatting && <div className="text-xs text-gray-500">{FORMATTING_NOTE}</div>}
        <div className="flex max-h-[60vh] flex-col gap-3 overflow-y-auto">
          {edits.map((edit, i) => (
            <div
              key={i}
              className="rounded border border-gray-200"
              data-testid="qnr-edit-block"
            >
              <div className="flex items-center gap-2 border-b border-gray-100 px-2 py-1">
                <Checkbox
                  checked={chosen[i]}
                  aria-label={`Accept change ${i + 1}`}
                  onChange={(e) =>
                    setChosen((c) => c.map((v, k) => (k === i ? e.target.checked : v)))
                  }
                >
                  Accept
                </Checkbox>
                <span className="text-xs text-gray-600">{editTitle(edit)}</span>
                {edit.wording && (
                  <Tag className="m-0" color="orange">
                    {WORDING_BADGE}
                  </Tag>
                )}
              </div>
              <div className="py-1">
                {edit.oldLines.map((text, k) => (
                  <DiffLine key={`o${k}`} number={edit.fromLine + k} text={text} removed />
                ))}
                {edit.newLines.map((text, k) => (
                  <DiffLine
                    key={`n${k}`}
                    number={afterStarts[i] + k}
                    text={text}
                    removed={false}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="text-xs text-gray-500">
          Removed lines show their line number now, added lines their number
          after the change. Apply makes the ticked changes as one step (one
          Undo takes them out). Nothing is saved until you save.
        </div>
      </div>
    </Modal>
  )
}

/**
 * The review of a questionnaire edit proposal: one block per edit (the old
 * lines struck through in red, the new ones in green, with line numbers and
 * a "Respondent wording changed" badge), each ticked to accept by default.
 * Apply makes the ticked edits in the editor as one transaction and marks
 * the proposal accepted, or stale when none could be made; a toast counts
 * the skipped ones. A proposal for another variant can't be applied here.
 * Open while `proposal` is set.
 */
const QnrProposalReview: React.FC<
  Omit<ReviewBodyProps, "proposal"> & { proposal: ProposalOf<"qnr-edit"> | null }
> = ({ proposal, ...rest }) =>
  proposal ? <ReviewBody key={proposal.id} proposal={proposal} {...rest} /> : null

export default QnrProposalReview
